import { useCallback, useRef } from "react";

const useSwipeGesture = (onSwipeLeft, onSwipeRight, threshold = 50, exclusions = []) => {
    const touchStartX = useRef(null);
    const touchStartY = useRef(null);
    const isMultiTouch = useRef(false);
    
    const handleTouchStart = useCallback((e) => {
        // Detect multi-touch (pinch/zoom gesture)
        if (e.touches.length > 1) {
            isMultiTouch.current = true;
            touchStartX.current = null;
            touchStartY.current = null;
            return;
        }
        
        // Reset multi-touch flag
        isMultiTouch.current = false;
        
        // Check if touch started on an excluded element
        const isExcluded = exclusions.some(className => 
            e.target.closest(`.${className}`)
        );
        
        if (isExcluded) {
            touchStartX.current = null;
            touchStartY.current = null;
            return;
        }
        
        touchStartX.current = e.touches[0].clientX;
        touchStartY.current = e.touches[0].clientY;
    }, [exclusions]);
    
    const handleTouchMove = useCallback((e) => {
        // If more than one finger touches during the gesture, mark as multi-touch
        if (e.touches.length > 1) {
            isMultiTouch.current = true;
            touchStartX.current = null;
            touchStartY.current = null;
        }
    }, []);
    
    const handleTouchEnd = useCallback((e) => {
        // Ignore if this was a multi-touch gesture
        if (isMultiTouch.current) {
            isMultiTouch.current = false;
            touchStartX.current = null;
            touchStartY.current = null;
            return;
        }
        
        if (!touchStartX.current || touchStartY.current === null) return;
        
        // Also check if touch ended on an excluded element
        const isExcluded = exclusions.some(className => 
            e.target.closest(`.${className}`)
        );
        
        if (isExcluded) {
            touchStartX.current = null;
            touchStartY.current = null;
            return;
        }
        
        // Get the end position directly from changedTouches
        const touchEndX = e.changedTouches[0].clientX;
        const touchEndY = e.changedTouches[0].clientY;
        
        // Check if video is zoomed
        const videoContainer = e.target.closest('.video-container');
        if (videoContainer) {
            const transformElement = videoContainer.querySelector('[style*="transform"]');
            if (transformElement) {
                const transform = transformElement.style.transform;
                if (transform && transform.includes('scale') && !transform.includes('scale(1)')) {
                    touchStartX.current = null;
                    touchStartY.current = null;
                    return;
                }
            }
        }
        
        const horizontalDistance = touchStartX.current - touchEndX;
        const verticalDistance = Math.abs(touchStartY.current - touchEndY);
        
        if (Math.abs(horizontalDistance) > threshold && Math.abs(horizontalDistance) > verticalDistance) {
            if (horizontalDistance > 0 && onSwipeLeft) {
                onSwipeLeft();
            } else if (horizontalDistance < 0 && onSwipeRight) {
                onSwipeRight();
            }
        }
        
        touchStartX.current = null;
        touchStartY.current = null;
    }, [onSwipeLeft, onSwipeRight, threshold, exclusions]);
    
    return { handleTouchStart, handleTouchMove, handleTouchEnd };
};

export default useSwipeGesture;